import {
  brandOptions,
  organizations,
  suppliers,
  contactPersons,
  adminUnits,
} from './itemsFixtureData';

/**
 * Deterministic probes for the D2 Playwright "Bulk edit clear fields" epic (dl-62704), the ITEMS
 * per-field-type-behavior spec (`frontend/e2e/src/pages/items/bulk-edit/clear-fields/
 * field-type-behavior.spec.ts`) — dl-62719/dl-62720/dl-62721/dl-62722/dl-62723. Isolable via
 * `?search=BulkEditClearField` (unique substring, absent from every generated/other fixture
 * name), with a sub-word per scenario for further narrowing.
 *
 * Split out as its own file (not appended to `bulkUpdateProbeFixtureData.ts`) so this spec's
 * fixtures are self-contained and reviewable in one place; wired into `itemsData` in `items.ts`
 * the same way that sibling file is. Base shape mirrors `bulkUpdateProbeBase` there.
 *
 * Field-to-scenario mapping (verified against the real bulk-edit modal code, not assumed from
 * Testmo prose — see the spec file's header for the full trace):
 * - dl-62719 (Testmo 2339508, "text/description field") — native `description` is
 *   `excludeFromBulkEdit: true` (`items/utils/fieldAccess.ts`), so the only free-text field the
 *   Items bulk-edit modal actually exposes is the "Internal notes" custom field (customFieldId 9,
 *   Text type, optional) — used here as the concrete stand-in.
 * - dl-62720 (Testmo 2339509, "optional number custom field") — "Target weight" (customFieldId
 *   13, Numeric, optional); contrasted against the native "Quantity" field (`defaultQty`), which
 *   `items/utils/validation.ts` marks `required: true` and can never be cleared.
 * - dl-62721 (Testmo 2339510, "date custom field") — "Launch date" (customFieldId 11, Date,
 *   optional) — same CF the sibling `itemBulkEditFieldValidation.spec.ts` uses for format
 *   validation, but THESE items carry an existing value (that spec's `bulkUpdateProbeDateA/B` do
 *   not) so the calendar-footer "Set empty" affordance is actually reachable.
 * - dl-62722 (Testmo 2339505, "optional single-select field") — the native "Unit" field
 *   (`adminUnit`), contrasted against "Status" (`isActive`), which is `required: true`.
 * - dl-62723 (Testmo 2339507, "Categories multiselect Set-empty footer") — the real "Categories"
 *   column has no MirageJS-mocked options endpoint (`GET /api/itemcategories` falls through to
 *   `mocks/browser.ts`'s general passthrough, a real network call that cannot succeed in the
 *   mock-only e2e environment — verified by grepping the whole mock layer, nothing handles that
 *   route), so its dropdown can never actually populate options to select/scroll through. The
 *   Testmo case's own manual steps explicitly allow "Categories OR a multi allowed-value CF" —
 *   this uses a dedicated multiselect CF ("Fabric Tags", customFieldId 97, 14 static allowed
 *   values) instead, which renders through the exact same `renderMultiselectField` /
 *   `MultiselectAutocomplete` component Categories itself would use, so the fixed-footer/
 *   scroll behaviour under test is identical either way.
 */
const bulkEditClearFieldBase = {
  organization: organizations[0],
  supplier: suppliers[0],
  companyContactPerson: contactPersons[0],
  supplierContactPerson: null,
  logo: null,
  suppliedBy: null,
  readyForExport: false,
  updatedAfterLastExport: false,
  lastUpdated: '2026-01-15T09:00:00',
  deadline: null,
  etd: null,
  erpItemId: null,
  nameGeneratorExtraText: null,
  physicalReferenceNumber: null,
  categories: [],
  brand: brandOptions[0],
  adminUnit: adminUnits[0],
  isActive: true,
  defaultQty: 5,
};

/**
 * The 14 static allowed values for the "Fabric Tags" multiselect CF (customFieldId 97) — enough
 * rows to exceed MUI Autocomplete's default `.MuiAutocomplete-listbox` `max-height: 40vh` (at the
 * suite's fixed 1920x1024 viewport, 40vh ≈ 410px; ~38px/row × 14 ≈ 530px), so the "stays visible
 * while scrolling" assertion (dl-62723) is exercised against a genuinely-scrolling list rather
 * than one that happens to fit.
 */
export const bulkClearMultiAllowedValues = Array.from({ length: 14 }, (_, i) => ({
  id: 9700 + i,
  label: `Fabric Tag ${String(i + 1).padStart(2, '0')}`,
  userDefinedId: null,
  parentId: null,
}));

/**
 * Additive custom-field definition layered on top of `itemCustomFieldDefinitions` via
 * `apiTransform({ url: '/api/meta/items/customfields', data: { customFieldDefinitions: [
 * ...itemCustomFieldDefinitions, bulkClearMultiCustomFieldDefinition] } })` — mirrors the
 * established `mandatoryExportCustomFieldDefinition` pattern in `bulkUpdateProbeFixtureData.ts`.
 * Not part of the base seed set, so every other bulk-edit CF column stays available alongside it.
 */
export const bulkClearMultiCustomFieldDefinition = {
  customFieldId: 97,
  name: 'Fabric Tags',
  userDefinedId: 'FABRICTAGS',
  type: 0, // CustomFieldType.AllowedValue
  isMandatory: false,
  isSupplierEditable: true,
  internalOnly: false,
  maxChar: null,
  isMultiLine: false,
  allowedDecimals: 0,
  isMultipleAllowedValue: true,
  allowedValues: bulkClearMultiAllowedValues,
  parentCustomFieldId: null,
  parentCustomFieldName: null,
};

/** Text-type CF value entry (customFieldId 9, "Internal notes"). */
const internalNoteCf = (id: number, value: string) => ({
  id,
  customFieldId: 9,
  name: 'Internal notes',
  userDefinedId: 'NOTES',
  type: 2,
  value,
  itemCustomFieldAllowedValueId: null,
  customFieldAllowedValueUserDefinedID: null,
  isMandatory: false,
  isSupplierEditable: false,
  internalOnly: true,
  maxChar: 500,
  isMultiLine: true,
  allowedDecimals: 0,
  isMultipleAllowedValue: false,
  parentCustomFieldId: null,
  parentCustomFieldName: null,
  allowedValues: [] as unknown[],
});

/** Numeric-type CF value entry (customFieldId 13, "Target weight"). */
const targetWeightCf = (id: number, value: number) => ({
  id,
  customFieldId: 13,
  name: 'Target weight',
  userDefinedId: 'WEIGHT',
  type: 4,
  value: String(value),
  itemCustomFieldAllowedValueId: null,
  customFieldAllowedValueUserDefinedID: null,
  isMandatory: false,
  isSupplierEditable: true,
  internalOnly: false,
  maxChar: null,
  isMultiLine: false,
  allowedDecimals: 2,
  isMultipleAllowedValue: false,
  parentCustomFieldId: null,
  parentCustomFieldName: null,
  allowedValues: [] as unknown[],
});

/** Date-type CF value entry (customFieldId 11, "Launch date"). ISO `value` — same convention as
 *  every other date-typed field in the mock (`lastUpdated`, `deadline`). */
const launchDateCf = (id: number, value: string) => ({
  id,
  customFieldId: 11,
  name: 'Launch date',
  userDefinedId: 'LAUNCH',
  type: 1,
  value,
  itemCustomFieldAllowedValueId: null,
  customFieldAllowedValueUserDefinedID: null,
  isMandatory: false,
  isSupplierEditable: true,
  internalOnly: false,
  maxChar: null,
  isMultiLine: false,
  allowedDecimals: 0,
  isMultipleAllowedValue: false,
  parentCustomFieldId: null,
  parentCustomFieldName: null,
  allowedValues: [] as unknown[],
});

/** AllowedValue (multiselect) CF value entry — one per selected tag (customFieldId 97). */
const fabricTagCf = (id: number, allowedValue: (typeof bulkClearMultiAllowedValues)[number]) => ({
  id,
  customFieldId: 97,
  name: 'Fabric Tags',
  userDefinedId: 'FABRICTAGS',
  type: 0,
  value: allowedValue.label,
  itemCustomFieldAllowedValueId: allowedValue.id,
  customFieldAllowedValueUserDefinedID: null,
  isMandatory: false,
  isSupplierEditable: true,
  internalOnly: false,
  maxChar: null,
  isMultiLine: false,
  allowedDecimals: 0,
  isMultipleAllowedValue: true,
  parentCustomFieldId: null,
  parentCustomFieldName: null,
  allowedValues: bulkClearMultiAllowedValues,
});

// ---------------------------------------------------------------------------
// dl-62719 / Testmo #2339508 — text (Internal notes CF) Set-empty
// ---------------------------------------------------------------------------
export const clearFieldTextA = {
  ...bulkEditClearFieldBase,
  id: 90260,
  name: 'BulkEditClearField Text A',
  userDefinedId: 'IT-90260',
  description: null,
  customFields: [internalNoteCf(90260009, 'Original internal note')],
};
export const clearFieldTextB = {
  ...clearFieldTextA,
  id: 90261,
  name: 'BulkEditClearField Text B',
  userDefinedId: 'IT-90261',
  customFields: [internalNoteCf(90261009, 'Original internal note')],
};

/** Contrast pair — no existing "Internal notes" value, so Set-empty must NOT appear (ties to the
 *  gating epic's dl-62711/Testmo #2339511, exercised in the sibling gating-eligibility spec; this
 *  file only needs the fixture data for THIS spec's own adversarial check). */
export const clearFieldTextEmptyA = {
  ...bulkEditClearFieldBase,
  id: 90262,
  name: 'BulkEditClearField Text Empty A',
  userDefinedId: 'IT-90262',
  description: null,
  customFields: [],
};
export const clearFieldTextEmptyB = {
  ...clearFieldTextEmptyA,
  id: 90263,
  name: 'BulkEditClearField Text Empty B',
  userDefinedId: 'IT-90263',
};

// ---------------------------------------------------------------------------
// dl-62720 / Testmo #2339509 — optional number CF (Target weight) Set-empty
// ---------------------------------------------------------------------------
export const clearFieldNumberA = {
  ...bulkEditClearFieldBase,
  id: 90264,
  name: 'BulkEditClearField Number A',
  userDefinedId: 'IT-90264',
  description: null,
  customFields: [targetWeightCf(90264013, 12.5)],
};
export const clearFieldNumberB = {
  ...clearFieldNumberA,
  id: 90265,
  name: 'BulkEditClearField Number B',
  userDefinedId: 'IT-90265',
  customFields: [targetWeightCf(90265013, 12.5)],
};

// ---------------------------------------------------------------------------
// dl-62721 / Testmo #2339510 — date CF (Launch date) Set-empty from calendar footer
// ---------------------------------------------------------------------------
export const clearFieldDateA = {
  ...bulkEditClearFieldBase,
  id: 90266,
  name: 'BulkEditClearField Date A',
  userDefinedId: 'IT-90266',
  description: null,
  customFields: [launchDateCf(90266011, '2026-03-10')],
};
export const clearFieldDateB = {
  ...clearFieldDateA,
  id: 90267,
  name: 'BulkEditClearField Date B',
  userDefinedId: 'IT-90267',
  customFields: [launchDateCf(90267011, '2026-03-10')],
};

// ---------------------------------------------------------------------------
// dl-62722 / Testmo #2339505 — optional single-select (Unit) Set-empty
// ---------------------------------------------------------------------------
export const clearFieldSelectA = {
  ...bulkEditClearFieldBase,
  id: 90268,
  name: 'BulkEditClearField Select A',
  userDefinedId: 'IT-90268',
  description: null,
  adminUnit: adminUnits[2],
  customFields: [],
};
export const clearFieldSelectB = {
  ...clearFieldSelectA,
  id: 90269,
  name: 'BulkEditClearField Select B',
  userDefinedId: 'IT-90269',
};

// ---------------------------------------------------------------------------
// dl-62723 / Testmo #2339507 — Categories multiselect (Fabric Tags stand-in) Set-empty footer
// ---------------------------------------------------------------------------
export const clearFieldMultiA = {
  ...bulkEditClearFieldBase,
  id: 90270,
  name: 'BulkEditClearField Multi A',
  userDefinedId: 'IT-90270',
  description: null,
  customFields: [
    fabricTagCf(90270971, bulkClearMultiAllowedValues[0]),
    fabricTagCf(90270972, bulkClearMultiAllowedValues[1]),
    fabricTagCf(90270973, bulkClearMultiAllowedValues[2]),
  ],
};
export const clearFieldMultiB = {
  ...clearFieldMultiA,
  id: 90271,
  name: 'BulkEditClearField Multi B',
  userDefinedId: 'IT-90271',
  customFields: [
    fabricTagCf(90271971, bulkClearMultiAllowedValues[0]),
    fabricTagCf(90271972, bulkClearMultiAllowedValues[1]),
    fabricTagCf(90271973, bulkClearMultiAllowedValues[2]),
  ],
};

/** All 12 fixture items in one array, for a single-import splice into `items.ts`'s `itemsData`
 *  (keeps that file's line count down — see its `max-lines` cap). */
export const clearFieldItems = [
  clearFieldTextA,
  clearFieldTextB,
  clearFieldTextEmptyA,
  clearFieldTextEmptyB,
  clearFieldNumberA,
  clearFieldNumberB,
  clearFieldDateA,
  clearFieldDateB,
  clearFieldSelectA,
  clearFieldSelectB,
  clearFieldMultiA,
  clearFieldMultiB,
];
